import { playbackDelayStore } from './playbackDelayStore';

export type TransferWallHold = {
  eventId: string;
  ohtId: string;
  action: 'PICKUP' | 'DROP';
  portId: string;
  /** Streamed transfer node, when the event publishes one. */
  x?: number;
  y?: number;
  /** Sim time the handoff happens at. The hold stays armed until the clock gets there. */
  simTimeS: number;
  /** Sim time the backend frees the vehicle. The pin ends here, never later. */
  resumeSimTimeS: number;
  /** Wall ms the frame arrived — diagnostics only; it does not start the hold. */
  receivedAtMs: number;
  /** Wall ms the vehicle reached the port and the handoff animation began. */
  startedAtMs: number;
  /** Real seconds the handoff animation occupies inside the dwell. */
  durationS: number;
  /** Latched fallback pose for events that publish no transfer coordinates. */
  anchorX?: number;
  anchorY?: number;
};

/** An armed hold has not reached its sim time yet, so it has no start. */
type StoredHold = Omit<TransferWallHold, 'startedAtMs'> & {
  startedAtMs: number | null;
  /** Vehicle clock reading captured when its route tried to leave the port. */
  frozenEffectiveT: number | null;
  /** Global clock reading at that same moment. */
  frozenRawT: number | null;
  /** Delay seconds this hold has already handed to `playbackDelayStore`. */
  contributedDelayS: number;
};

/**
 * Transfer handoffs, which park the vehicle at the port for `holdWallSec`.
 *
 * Two clocks meet here. `holdWallSec` is how long an operator watches the
 * handoff, so it owns the pin: PICKUP and DROP both stand still for exactly
 * that many real seconds regardless of playback speed.
 *
 * The backend dwell (`simTimeS` → `resumeSimTimeS`) cannot own it, because the
 * two disagree. A PICKUP publishes a matching stationary leg, so its dwell and
 * `holdWallSec` describe the same pause. A DROP publishes only how long the
 * station is busy putting the FOUP away, and routes the vehicle onward within a
 * few sim seconds — far sooner than the handoff is over.
 *
 * Pinning across that departure would let the route run out from under the
 * vehicle and teleport it on release, so the pin also freezes this vehicle's
 * own clock at the moment its route tried to leave. It resumes from the port
 * and drives the rest of the route, simply running that much behind the
 * timeline. A vehicle whose route keeps it parked anyway, like every PICKUP,
 * never freezes and never falls behind.
 *
 * `transfer_event` frames are also lookahead: the handoff they describe can be
 * minutes of sim time ahead of the tick that delivered them, so a hold arms on
 * arrival and starts when the vehicle's own clock reaches `simTimeS`.
 */
class TransferWallHoldStore {
  private holds = new Map<string, StoredHold>();

  begin(
    input: Omit<TransferWallHold, 'startedAtMs' | 'durationS' | 'receivedAtMs'> & {
      holdWallSec: number;
    },
    receivedAtMs: number
  ): void {
    if (!(input.holdWallSec > 0) || !Number.isFinite(input.holdWallSec)) return;
    this.holds.set(input.ohtId, {
      eventId: input.eventId,
      ohtId: input.ohtId,
      action: input.action,
      portId: input.portId,
      x: input.x,
      y: input.y,
      simTimeS: input.simTimeS,
      resumeSimTimeS: input.resumeSimTimeS,
      receivedAtMs,
      startedAtMs: null,
      durationS: input.holdWallSec,
      frozenEffectiveT: null,
      frozenRawT: null,
      contributedDelayS: 0,
    });
  }

  /**
   * The live hold for this OHT, or null when it has none, its handoff is still
   * in the future, or `holdWallSec` real seconds have elapsed since it reached
   * the port. `simTimeS` is the vehicle's own playback time; omit it only where
   * no clock is available, in which case an armed hold stays dormant rather
   * than firing early.
   */
  get(
    ohtId: string,
    nowMs: number = performance.now(),
    simTimeS?: number
  ): TransferWallHold | null {
    const hold = this.holds.get(ohtId);
    if (!hold) return null;
    if (simTimeS == null || simTimeS < hold.simTimeS) return null;

    if (hold.startedAtMs == null) hold.startedAtMs = nowMs;
    if (nowMs - hold.startedAtMs >= hold.durationS * 1000) {
      this.holds.delete(ohtId);
      return null;
    }
    return hold as TransferWallHold;
  }

  /**
   * Hold this vehicle's clock still for as long as it is pinned, and report the
   * playback time the rest of the frame should sample.
   *
   * `scheduleMoving` says whether its route is currently trying to drive it
   * somewhere. Until that happens the route agrees with the pin and the clock
   * runs untouched; from the first frame it disagrees, the elapsed simulation
   * time is handed to `playbackDelayStore` so the vehicle keeps reading the
   * instant it was still at the port. Calling this more than once per frame,
   * from the 2D and 3D overlays alike, adds the same delay only once.
   */
  pinTimeline(
    hold: TransferWallHold | null,
    rawSimTimeS: number,
    effectiveSimTimeS: number,
    scheduleMoving: boolean
  ): number {
    if (!hold) return effectiveSimTimeS;
    const stored = this.holds.get(hold.ohtId);
    if (!stored) return effectiveSimTimeS;

    if (stored.frozenEffectiveT == null || stored.frozenRawT == null) {
      if (!scheduleMoving) return effectiveSimTimeS;
      stored.frozenEffectiveT = effectiveSimTimeS;
      stored.frozenRawT = rawSimTimeS;
    }

    const owed = rawSimTimeS - stored.frozenRawT;
    const increment = owed - stored.contributedDelayS;
    if (increment > 0) {
      stored.contributedDelayS = owed;
      playbackDelayStore.addDelay(hold.ohtId, increment);
    }
    return stored.frozenEffectiveT;
  }

  /**
   * Where the vehicle stands for the length of the hold.
   *
   * `preferred` is the known operating point — the streamed transfer node or
   * the layout dock for the port. Many stocker events publish neither, and
   * those still have to stop the vehicle at the port, so the pose it had when
   * the hold began is latched and reused.
   */
  holdPoint(
    hold: TransferWallHold | null,
    preferred: { x: number; y: number } | null | undefined,
    current: { x: number; y: number }
  ): { x: number; y: number } | null {
    if (!hold) return null;
    if (preferred) return preferred;
    if (hold.anchorX == null || hold.anchorY == null) {
      hold.anchorX = current.x;
      hold.anchorY = current.y;
    }
    return { x: hold.anchorX, y: hold.anchorY };
  }

  reset(): void {
    this.holds.clear();
  }
}

export const transferWallHoldStore = new TransferWallHoldStore();
